// src/components/LayerPanel/ActiveLayers.jsx
//
// The unified "ACTIVE LAYERS" section. It sits above the tabs and is
// persistent across tab changes — it reflects every active layer on the map:
//   - imagery stack entries get the full row (drag-reorder, pin/follow,
//     hover-revealed opacity, zoom-to, effective date, remove);
//   - non-imagery layers (features, product, user) get a minimal row
//     (name + scope tag + remove).
//
// Capped at ~40% of the panel height with its own scroll, so the tabs stay
// anchored no matter how many layers are active.

import { useMemo, useState, useRef, useEffect } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical, Pin, Clock, SlidersHorizontal, LocateFixed, X,
  BookMarked, ChevronDown, Palette, RotateCcw,
} from "lucide-react";

import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { useMapActions } from "../../stores/useMapActions";
import { useLayerCoverageStore } from "../../stores/useLayerCoverageStore";
import { formatAcquisitionDate } from "../../layers/imagery/formatImageryDate";
import { effectiveDateFor } from "../../layers/imagery/effectiveDate";
import useActiveLayers from "./useActiveLayers";
import SetsFlyout from "./SetsFlyout";
import FeatureColorSwatch from "./FeatureColorSwatch";
import { isFollowingCursor } from "../../layers/imagery/effectiveDate";

function cx(...c) { return c.filter(Boolean).join(" "); }

// Short scope tags for the minimal non-imagery rows.
const SCOPE_TAG = {
  project: "FEAT",
  product: "PROD",
  user: "MINE",
  feature: "FEAT",
};

// Identity defaults — mirrors IDENTITY_ADJUST in useImageryStackStore. Used to
// render legacy entries (entry.adjust === undefined) at no-op positions.
const ADJUST_IDENTITY = { brightnessMin: 0, contrast: 0, saturation: 0, hueRotate: 0 };

// The four MapLibre raster paint props as labeled sliders. Ranges match the
// store's clamp ranges exactly. `fmt` renders the readout next to each slider.
const ADJUST_FIELDS = [
  { key: "brightnessMin", label: "Brightness", min: 0, max: 1, step: 0.05, fmt: (v) => v.toFixed(2) },
  { key: "contrast", label: "Contrast", min: -1, max: 1, step: 0.05, fmt: (v) => v.toFixed(2) },
  { key: "saturation", label: "Saturation", min: -1, max: 1, step: 0.05, fmt: (v) => v.toFixed(2) },
  { key: "hueRotate", label: "Hue", min: 0, max: 359, step: 1, fmt: (v) => `${Math.round(v)}°` },
];

/* ─────────────────────────────────────────────
   RASTER ADJUST — brightness/contrast/saturation/hue
   Sits adjacent to the opacity slider. Values persist in the store; only the
   expand/collapse is session-local. Reuses the opacity slider's markup.
───────────────────────────────────────────── */
function RasterAdjustControls({ entry, label }) {
  const setLayerAdjust = useImageryStackStore((s) => s.setLayerAdjust);
  const resetLayerAdjust = useImageryStackStore((s) => s.resetLayerAdjust);

  const adjust = entry.adjust ?? ADJUST_IDENTITY;
  const isIdentity = ADJUST_FIELDS.every(
    (f) => (adjust[f.key] ?? 0) === ADJUST_IDENTITY[f.key]
  );

  return (
    <div className="px-3 pb-2 pt-0 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-semibold font-mono uppercase tracking-wider text-fg-secondary">
          Display Adjust
        </span>
        <button
          onClick={() => resetLayerAdjust(entry.layer_id)}
          disabled={isIdentity}
          title="Reset adjustments"
          className={cx(
            "flex items-center gap-1 px-1 py-0.5 rounded text-[9px] transition",
            isIdentity
              ? "text-slate-600 cursor-default"
              : "text-fg-secondary hover:text-accent-action hover:bg-slate-700"
          )}
        >
          <RotateCcw size={10} />
          Reset
        </button>
      </div>
      {ADJUST_FIELDS.map((f) => (
        <div key={f.key} className="flex items-center gap-2">
          <span className="text-[9px] text-fg-secondary w-14 flex-shrink-0">
            {f.label}
          </span>
          <input
            type="range"
            min={f.min}
            max={f.max}
            step={f.step}
            value={adjust[f.key] ?? 0}
            onChange={(e) =>
              setLayerAdjust(entry.layer_id, { [f.key]: Number(e.target.value) })
            }
            aria-label={`${f.label} for ${label}`}
            className="flex-1 accent-accent-info"
          />
          <span className="text-[9px] font-mono text-fg-secondary tabular-nums w-8 text-right">
            {f.fmt(adjust[f.key] ?? 0)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   IMAGERY ROW — full controls
───────────────────────────────────────────── */
function ActiveImageryRow({ entry, series, effectiveDate, acquisition }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: entry.layer_id });
  const setLayerTimeMode = useImageryStackStore((s) => s.setLayerTimeMode);
  const setLayerOpacity = useImageryStackStore((s) => s.setLayerOpacity);
  const removeLayer = useImageryStackStore((s) => s.removeLayer);
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);

  const [hovered, setHovered] = useState(false);
  const [showOpacity, setShowOpacity] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);

  // Faithful to the old TrayItem: a "follow_cursor" entry shows the Clock and
  // toggles to "pinned"; anything else shows the Pin and toggles to follow.
  // Shared predicate — "global" is also a following mode; see effectiveDate.js.
  const isFollow = isFollowingCursor(entry);
  const bounds = acquisition?.bounds;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowOpacity(false); setShowAdjust(false); }}
      className="rounded-md border border-slate-700 bg-bg-softer"
    >
      <div className="flex items-center gap-1.5 px-2 py-[5px]">
        <span
          {...attributes}
          {...listeners}
          className="text-fg-secondary hover:text-slate-400 cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <GripVertical size={12} />
        </span>
        <span className="w-2 h-2 rounded-full bg-accent-info flex-shrink-0" />
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-[11px] text-gray-200 truncate">
            {series?.display_name ?? entry.series_key}
          </div>
          <div className="text-[9px] text-fg-secondary font-mono tabular-nums truncate">
            {effectiveDate
              ? formatAcquisitionDate(effectiveDate)
              : "no acquisition at cursor"}
          </div>
        </div>

        {/* Icon row — pin/clock and X always visible; opacity + zoom on hover. */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() =>
              setLayerTimeMode(entry.layer_id, isFollow ? "pinned" : "follow_cursor")
            }
            title={
              isFollow
                ? "Following time cursor — click to pin"
                : "Pinned to date — click to follow cursor"
            }
            className="p-1 rounded text-accent-info bg-accent-info/10 hover:bg-accent-info/20"
          >
            {isFollow ? <Clock size={11} /> : <Pin size={11} />}
          </button>

          {hovered && (
            <>
              <button
                onClick={() => setShowOpacity((v) => !v)}
                title="Opacity"
                className={cx(
                  "p-1 rounded transition",
                  showOpacity
                    ? "bg-slate-700 text-accent-action"
                    : "hover:bg-slate-700 text-fg-secondary hover:text-gray-200"
                )}
              >
                <SlidersHorizontal size={11} />
              </button>
              <button
                onClick={() => setShowAdjust((v) => !v)}
                title="Display adjust (brightness/contrast/saturation/hue)"
                className={cx(
                  "p-1 rounded transition",
                  showAdjust
                    ? "bg-slate-700 text-accent-action"
                    : "hover:bg-slate-700 text-fg-secondary hover:text-gray-200"
                )}
              >
                <Palette size={11} />
              </button>
              {bounds && (
                <button
                  onClick={() =>
                    zoomToBounds([bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]])
                  }
                  title="Zoom to layer"
                  className="p-1 rounded hover:bg-slate-700 text-fg-secondary hover:text-accent-action"
                >
                  <LocateFixed size={11} />
                </button>
              )}
            </>
          )}

          <button
            onClick={() => removeLayer(entry.layer_id)}
            title="Remove from map"
            className="p-1 rounded hover:bg-slate-700 text-fg-secondary hover:text-red-400"
          >
            <X size={11} />
          </button>
        </div>
      </div>

      {showOpacity && (
        <div className="px-3 pb-2 pt-0 flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={entry.opacity}
            onChange={(e) => setLayerOpacity(entry.layer_id, Number(e.target.value))}
            aria-label={`Opacity for ${series?.display_name ?? entry.series_key}`}
            className="flex-1 accent-accent-info"
          />
          <span className="text-[9px] font-mono text-fg-secondary tabular-nums w-7 text-right">
            {Math.round(entry.opacity * 100)}%
          </span>
        </div>
      )}

      {showAdjust && (
        <RasterAdjustControls
          entry={entry}
          label={series?.display_name ?? entry.series_key}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   NON-IMAGERY ROW — minimal
───────────────────────────────────────────── */
function ActiveLayerRow({ layer, onRemove }) {
  const coverage = useLayerCoverageStore((s) => s.coverage);
  // Only render the 0-in-AOI hint when:
  //   - we have coverage (AOI mode + fetch succeeded — null in global mode),
  //   - the layer carries an assetType in the coverage map,
  //   - its count is explicitly zero (count:null = timeout fallback, no hint).
  const isZeroInAoi =
    coverage &&
    layer.assetType &&
    coverage[layer.assetType]?.count === 0;

  return (
    <div className="flex items-center gap-2 px-2 py-[6px] rounded-md border border-slate-700 bg-bg-softer">
      {layer._source === "feature" && layer.assetType ? (
        <FeatureColorSwatch assetType={layer.assetType} size={11} />
      ) : (
        <span className="w-2 h-2 rounded-full bg-accent-info/70 flex-shrink-0" />
      )}
      <span className="flex-1 min-w-0 text-[11px] text-gray-200 truncate">
        {layer.label}
      </span>
      {isZeroInAoi && (
        <span
          className="w-1.5 h-1.5 rounded-full bg-amber-400/80 flex-shrink-0"
          title="0 features in current AOI"
        />
      )}
      <span className="text-[8px] font-mono px-1 py-[1px] rounded bg-slate-800 text-fg-secondary flex-shrink-0">
        {SCOPE_TAG[layer.scope] ?? (layer.scope ?? "").toUpperCase()}
      </span>
      <button
        onClick={() => onRemove(layer)}
        title="Turn off"
        className="p-1 rounded hover:bg-slate-700 text-fg-secondary hover:text-red-400 flex-shrink-0"
      >
        <X size={11} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ACTIVE LAYERS
───────────────────────────────────────────── */
export default function ActiveLayers() {
  const catalog = useImageryStackStore((s) => s.catalog);
  const stack = useImageryStackStore((s) => s.stack);
  const cursorDate = useImageryStackStore((s) => s.cursor.global_date);
  const reorderStack = useImageryStackStore((s) => s.reorderStack);

  const { activeLayers, deactivateLayer } = useActiveLayers();

  const sensors = useSensors(useSensor(PointerSensor));
  const allSeries = useMemo(() => catalog.series ?? [], [catalog]);

  // Sets flyout — anchored to the "Sets" trigger in the section header.
  const [setsOpen, setSetsOpen] = useState(false);
  const setsRef = useRef(null);
  useEffect(() => {
    if (!setsOpen) return;
    function onOutside(e) {
      if (setsRef.current && !setsRef.current.contains(e.target)) {
        setSetsOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [setsOpen]);

  // Imagery stack entries resolved to series + effective acquisition.
  const imageryEntries = useMemo(() => {
    return stack.map((entry) => {
      const series = allSeries.find((s) => s.series_key === entry.series_key);
      const effectiveDate = series
        ? effectiveDateFor(series, entry, cursorDate)
        : null;
      const acquisition =
        series && effectiveDate
          ? series.acquisitions.find((a) => a.acquisition_date === effectiveDate)
          : null;
      return { entry, series, effectiveDate, acquisition };
    });
  }, [stack, allSeries, cursorDate]);

  // Display order is top-of-map first: the stack is ordered by z_index
  // ascending (z=0 draws at the BOTTOM of the map), so we reverse it for the
  // panel — the row at the top of the list is the layer drawn on top of the
  // map, matching every layer-panel convention. reorderStack still operates by
  // layer_id, so drag-reorder stays correct under the reversal.
  const orderedImageryEntries = useMemo(
    () => [...imageryEntries].reverse(),
    [imageryEntries]
  );

  // Non-imagery active layers (features, product, user) — minimal rows.
  const otherLayers = useMemo(
    () => activeLayers.filter((l) => l.scope !== "imagery"),
    [activeLayers]
  );

  const ids = useMemo(
    () => orderedImageryEntries.map((e) => e.entry.layer_id),
    [orderedImageryEntries]
  );

  const isEmpty = imageryEntries.length === 0 && otherLayers.length === 0;

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    reorderStack(active.id, over.id);
  }

  return (
    <div className="flex-shrink-0 flex flex-col max-h-[40%] border-b border-slate-800 bg-bg-softer">
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2.5 pt-2 pb-1">
        <span className="text-[10px] font-bold font-mono uppercase tracking-[0.14em] text-gray-400">
          Active Layers
        </span>

        {/* Sets flyout trigger — right-justified next to the section label. */}
        <div className="relative" ref={setsRef}>
          <button
            type="button"
            onClick={() => setSetsOpen((v) => !v)}
            aria-expanded={setsOpen}
            className={cx(
              "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition",
              setsOpen
                ? "bg-slate-800 text-gray-200"
                : "text-gray-400 hover:text-gray-200 hover:bg-slate-800/60"
            )}
            title="Layer sets"
          >
            <BookMarked size={11} />
            Sets
            <ChevronDown size={10} />
          </button>
          {setsOpen && <SetsFlyout onClose={() => setSetsOpen(false)} />}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
        {isEmpty && (
          <div className="px-0.5 text-[10px] text-fg-secondary">
            No active layers — turn layers on from the tabs below.
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {orderedImageryEntries.map(({ entry, series, effectiveDate, acquisition }) => (
                <ActiveImageryRow
                  key={entry.layer_id}
                  entry={entry}
                  series={series}
                  effectiveDate={effectiveDate}
                  acquisition={acquisition}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {otherLayers.length > 0 && (
          <div className="space-y-1">
            {otherLayers.map((layer) => (
              <ActiveLayerRow
                key={layer.id}
                layer={layer}
                onRemove={deactivateLayer}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
